import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useOffline } from '../../context/OfflineContext';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import { getCacheStats, clearAttachmentCache } from '../../services/attachmentCache';
import { tripsApi } from '../../services/api';
import { CardErrorBoundary } from '../UI/ErrorBoundary';
import AccountCard from './AccountCard';
import AppearanceCard from './AppearanceCard';
import PreferencesCard from './PreferencesCard';
import AboutCard from './AboutCard';
import OfflineAccessCard from './OfflineAccessCard';
import DataManagementCard from './DataManagementCard';
import TripsCard from './TripsCard';

const APP_VERSION = '2.9.1';

/**
 * Settings - Main settings page with preference and cache management
 */
export default function Settings() {
  const { user, updatePreferences } = useAuth();
  const { isOnline } = useOffline();
  const { theme, setTheme } = useTheme();
  const toast = useToast();
  
  // Form state
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    home_timezone: user?.home_timezone || 'Europe/Dublin',
    base_currency: user?.base_currency || 'EUR',
  });
  
  // Cache state
  const [clearingCache, setClearingCache] = useState(false);
  const [attachmentCacheStats, setAttachmentCacheStats] = useState(null);

  // Trips state
  const [trips, setTrips] = useState([]);
  const [tripsLoading, setTripsLoading] = useState(true);

  useEffect(() => {
    loadCacheStats();
    if (isOnline) {
      loadTrips();
    } else {
      setTripsLoading(false);
    }
  }, [isOnline]);

  async function loadTrips() {
    try {
      setTripsLoading(true);
      const data = await tripsApi.list();
      setTrips(data);
    } catch (err) {
      console.error('Failed to load trips:', err);
    } finally {
      setTripsLoading(false);
    }
  }

  async function handleCreateTrip(tripData) {
    try {
      const newTrip = await tripsApi.create(tripData);
      setTrips(prev => [newTrip, ...prev]);
      toast.success('Trip created');
    } catch (err) {
      toast.error('Failed to create trip');
      throw err;
    }
  }

  async function handleUpdateTrip(id, tripData) {
    try {
      const updated = await tripsApi.update(id, tripData);
      setTrips(prev => prev.map(t => t.id === id ? updated : t));
      toast.success('Trip updated');
    } catch (err) {
      toast.error('Failed to update trip');
      throw err;
    }
  }

  async function handleDeleteTrip(id) {
    try {
      await tripsApi.delete(id);
      setTrips(prev => prev.filter(t => t.id !== id));
      toast.success('Trip deleted');
    } catch (err) {
      toast.error('Failed to delete trip');
      throw err;
    }
  }

  async function loadCacheStats() {
    try {
      const stats = await getCacheStats();
      setAttachmentCacheStats(stats);
    } catch (err) {
      console.error('Failed to load cache stats:', err);
    }
  }

  async function handleSave() {
    setSaving(true);
    
    try {
      await updatePreferences(preferences);
      toast.success('Preferences saved successfully!');
    } catch (err) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  }

  async function handleClearAllCache() {
    setClearingCache(true);
    try {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_API_CACHE' });
      }
      if ('caches' in window) {
        await caches.delete('api-v1.1');
      }
      const databases = await indexedDB.databases();
      for (const db of databases) {
        if (db.name?.startsWith('travel-itinerary')) {
          indexedDB.deleteDatabase(db.name);
        }
      }
      setAttachmentCacheStats({ count: 0, sizeMB: '0.00', maxSizeMB: '100', percentUsed: '0' });
      toast.success('Cache cleared successfully!');
    } catch (err) {
      toast.error('Failed to clear cache');
    } finally {
      setClearingCache(false);
    }
  }

  async function handleClearAttachmentsOnly() {
    try {
      await clearAttachmentCache();
      await loadCacheStats();
      toast.success('Attachment cache cleared!');
    } catch (err) {
      toast.error('Failed to clear attachment cache');
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-display font-medium" style={{ color: 'var(--color-text-primary)' }}>
          Settings
        </h2>
        <p className="mt-1" style={{ color: 'var(--color-text-muted)' }}>
          Manage your preferences and account settings
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          <CardErrorBoundary title="Failed to load account info">
            <AccountCard user={user} />
          </CardErrorBoundary>
          <CardErrorBoundary title="Failed to load appearance settings">
            <AppearanceCard theme={theme} onThemeChange={setTheme} />
          </CardErrorBoundary>
          <CardErrorBoundary title="Failed to load preferences">
            <PreferencesCard
              preferences={preferences}
              onPreferencesChange={setPreferences}
              onSave={handleSave}
              saving={saving}
              isOnline={isOnline}
            />
          </CardErrorBoundary>
          {isOnline && (
            <CardErrorBoundary title="Failed to load trips">
              <TripsCard
                trips={trips}
                onCreateTrip={handleCreateTrip}
                onUpdateTrip={handleUpdateTrip}
                onDeleteTrip={handleDeleteTrip}
                loading={tripsLoading}
              />
            </CardErrorBoundary>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          <CardErrorBoundary title="Failed to load app info">
            <AboutCard version={APP_VERSION} />
          </CardErrorBoundary>
          <CardErrorBoundary title="Failed to load offline settings">
            <OfflineAccessCard
              isOnline={isOnline}
              attachmentCacheStats={attachmentCacheStats}
              clearingCache={clearingCache}
              onClearAllCache={handleClearAllCache}
              onClearAttachmentsOnly={handleClearAttachmentsOnly}
            />
          </CardErrorBoundary>
          <CardErrorBoundary title="Failed to load data management">
            <DataManagementCard />
          </CardErrorBoundary>
        </div>
      </div>
    </div>
  );
}
